import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";

import {
  getInvitationByTokenService,
  getInvitationsService,
  postAcceptInviteService,
  postDealInviteService,
  postOrgInviteService,
  postResendInvitationService,
  postRevokeInvitationService,
} from "@/services/invitations.service";
import { IAPIResponse } from "@/services/api.types";
import {
  AcceptInviteFormData,
  AcceptInviteResponse,
  DealInviteFormData,
  Invitation,
  InvitationTokenResponse,
  OrgInviteFormData,
  ResendInvitationPayload,
  ResendInvitationResponse,
  RevokeInvitationPayload,
  RevokeInvitationResponse,
} from "@/types/invitations.types";

export const invitationsQueryKeys = {
  all: () => ["invitations"] as const,
  lists: () => [...invitationsQueryKeys.all(), "list"] as const,
  byToken: (token: string) => [...invitationsQueryKeys.all(), "token", token] as const,
};

export const usePostOrgInvite = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<Invitation>, AxiosError, OrgInviteFormData>({
    mutationFn: postOrgInviteService,
    retry: false,
    onSuccess: res => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: invitationsQueryKeys.lists() });
    },
    onError: err => toast.error(err.message),
  });
};

export const usePostDealInvite = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<Invitation>, AxiosError, DealInviteFormData>({
    mutationFn: postDealInviteService,
    retry: false,
    onSuccess: res => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: invitationsQueryKeys.lists() });
    },
    onError: err => toast.error(err.message),
  });
};

export const useGetInvitations = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<Invitation[], AxiosError>({
    queryKey: invitationsQueryKeys.lists(),
    queryFn: getInvitationsService,
    enabled,
  });
};

export const usePostRevokeInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation<
    IAPIResponse<RevokeInvitationResponse>,
    AxiosError,
    { invitationId: string; payload: RevokeInvitationPayload }
  >({
    mutationFn: ({ invitationId, payload }) => postRevokeInvitationService(invitationId, payload),
    retry: false,
    onSuccess: res => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: invitationsQueryKeys.lists() });
    },
    onError: err => toast.error(err.message),
  });
};

export const usePostResendInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation<
    IAPIResponse<ResendInvitationResponse>,
    AxiosError,
    { invitationId: string; payload: ResendInvitationPayload }
  >({
    mutationFn: ({ invitationId, payload }) => postResendInvitationService(invitationId, payload),
    retry: false,
    onSuccess: res => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: invitationsQueryKeys.lists() });
    },
    onError: err => toast.error(err.message),
  });
};

export const useGetInvitationByToken = (token: string) => {
  return useQuery<InvitationTokenResponse, AxiosError>({
    queryKey: invitationsQueryKeys.byToken(token),
    queryFn: () => getInvitationByTokenService(token),
    enabled: !!token,
    retry: false,
  });
};

export const usePostAcceptInvite = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<AcceptInviteResponse>, AxiosError, { token: string; payload: AcceptInviteFormData }>({
    mutationFn: ({ token, payload }) => postAcceptInviteService(token, payload),
    retry: false,
    onSuccess: res => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: invitationsQueryKeys.all() });
    },
    onError: err => toast.error(err.message),
  });
};
